import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface OrderTrackingMapProps {
  orderId: string;
  customerLocation: { lat: number; lng: number };
  partnerLocation?: { lat: number; lng: number };
  storeLocation?: { lat: number; lng: number };
  orderStatus: string;
}

// Custom component to fit bounds
const FitBounds: React.FC<{ locations: Array<{ lat: number; lng: number }> }> = ({ locations }) => {
  const map = useMap();
  
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => [loc.lat, loc.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);
  
  return null;
};

const OrderTrackingMap: React.FC<OrderTrackingMapProps> = ({
  orderId,
  customerLocation,
  partnerLocation,
  storeLocation,
  orderStatus
}) => {
  const [routeCoordinates, setRouteCoordinates] = useState<Array<[number, number]>>([]);

  // Calculate route (straight line for now, can use routing service)
  useEffect(() => {
    if (partnerLocation && customerLocation) {
      // Simple straight line route
      setRouteCoordinates([
        [partnerLocation.lat, partnerLocation.lng],
        [customerLocation.lat, customerLocation.lng]
      ]);
      
      // Optional: Use OSRM routing service for real roads
      // fetch(`https://router.project-osrm.org/route/v1/driving/${partnerLocation.lng},${partnerLocation.lat};${customerLocation.lng},${customerLocation.lat}?overview=full&geometries=geojson`)
      //   .then(res => res.json())
      //   .then(data => {
      //     if (data.routes && data.routes[0]) {
      //       const coords = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
      //       setRouteCoordinates(coords);
      //     }
      //   })
      //   .catch(err => console.error('Routing error:', err));
    }
  }, [partnerLocation, customerLocation]);

  // Custom icons
  const customerIcon = new L.DivIcon({
    html: `<div style="background: #4CAF50; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); font-size: 20px;">🏠</div>`,
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  });

  const partnerIcon = new L.DivIcon({
    html: `<div style="background: #6366f1; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); font-size: 20px; animation: bounce 1s infinite;">🛵</div>`,
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  });

  const storeIcon = new L.DivIcon({
    html: `<div style="background: #2196F3; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); font-size: 20px;">🏪</div>`,
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  });

  // Prepare locations for bounds
  const locations = [customerLocation];
  if (partnerLocation) locations.push(partnerLocation);
  if (storeLocation) locations.push(storeLocation);

  const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '10px',
    overflow: 'hidden'
  };

  return (
    <div style={{ width: '100%', marginTop: '20px' }}>
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
      
      <MapContainer
        center={[customerLocation.lat, customerLocation.lng]}
        zoom={14}
        style={mapContainerStyle}
      >
        {/* OpenStreetMap Tiles - 100% FREE, No API Key! */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Fit bounds to show all markers */}
        <FitBounds locations={locations} />

        {/* Customer Location Marker (Home) */}
        <Marker
          position={[customerLocation.lat, customerLocation.lng]}
          icon={customerIcon}
        >
          <Popup>
            <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
              <div style={{ fontSize: '16px' }}>🏠</div>
              <div>Your Location</div>
            </div>
          </Popup>
        </Marker>

        {/* Partner Location Marker (Delivery Person) */}
        {partnerLocation && (
          <Marker
            position={[partnerLocation.lat, partnerLocation.lng]}
            icon={partnerIcon}
          >
            <Popup>
              <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#6366f1' }}>
                <div style={{ fontSize: '16px' }}>🛵</div>
                <div>Delivery Partner</div>
                <div style={{ fontSize: '12px', marginTop: '5px' }}>On the way!</div>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Store Location Marker */}
        {storeLocation && (
          <Marker
            position={[storeLocation.lat, storeLocation.lng]}
            icon={storeIcon}
          >
            <Popup>
              <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#2196F3' }}>
                <div style={{ fontSize: '16px' }}>🏪</div>
                <div>Store Location</div>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Show route if partner location available */}
        {routeCoordinates.length > 0 && (
          <Polyline
            positions={routeCoordinates}
            pathOptions={{
              color: '#6366f1',
              weight: 5,
              opacity: 0.8
            }}
          />
        )}
      </MapContainer>

      {/* Map Legend */}
      <div style={{
        marginTop: '15px',
        padding: '15px',
        background: '#f5f5f5',
        borderRadius: '10px',
        display: 'flex',
        justifyContent: 'space-around',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>🏠</span>
          <span style={{ fontSize: '14px', color: '#666' }}>Your Location</span>
        </div>
        {partnerLocation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🛵</span>
            <span style={{ fontSize: '14px', color: '#666' }}>Delivery Partner</span>
          </div>
        )}
        {storeLocation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🏪</span>
            <span style={{ fontSize: '14px', color: '#666' }}>Store</span>
          </div>
        )}
      </div>

      {/* Tracking Status */}
      <div style={{
        marginTop: '15px',
        padding: '15px',
        background: orderStatus === 'Delivered' ? '#4CAF50' : '#6366f1',
        color: 'white',
        borderRadius: '10px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '16px'
      }}>
        {orderStatus === 'Pending' && '⏳ Order Placed - Waiting for Partner'}
        {orderStatus === 'Accepted' && '✅ Partner Accepted - Preparing Items'}
        {orderStatus === 'Picked' && '📦 Items Picked - On the Way'}
        {orderStatus === 'At Customer Location' && '🎯 Partner Arrived at Your Location'}
        {orderStatus === 'Delivered' && '✅ Order Delivered Successfully!'}
        {!['Pending', 'Accepted', 'Picked', 'At Customer Location', 'Delivered'].includes(orderStatus) && `Status: ${orderStatus}`}
      </div>

      {/* FREE OpenStreetMap Notice */}
      <div style={{
        marginTop: '15px',
        padding: '12px',
        background: '#d4edda',
        border: '1px solid #28a745',
        borderRadius: '8px',
        fontSize: '12px',
        color: '#155724',
        textAlign: 'center'
      }}>
        <strong>✅ 100% FREE Tracking!</strong> Powered by OpenStreetMap - No API key needed, unlimited usage forever.
      </div>
    </div>
  );
};

export default OrderTrackingMap;
